<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:user',
	name: '$t:blocks.input_user_m2o.name',
	description: '$t:blocks.input_user_m2o.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{ name: 'fieldId', label: '$t:blocks.common.props.field' },
		{ name: 'referencedItemKey', label: '$t:blocks.common.props.referenced_item' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],
	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { UserAvatarUser } from '#layers/access/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId, Uuid } from '#layers/data-model/public';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccessDisplayNames, UserAvatar, UserPicker } from '#layers/access/public';
import { defineBlock } from '#layers/block-engine/public';
import M2ODeselectButton from '#layers/blocks/components/Relation/M2ODeselectButton.vue';
import { useM2OState } from '#layers/blocks/composables/relation/use-m2o-state';
import { toCompositePrimaryKey } from '#layers/data-model/public';
import { injectFormBlockContext, useM2OPermissions, useRelationFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface InputUserM2OBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
	referencedItemKey?: CompositePrimaryKey;
	showRemove?: boolean;
}

const {
	showRemove = true,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	fieldId,
} = defineProps<InputUserM2OBlockProps>();

;

const { t } = useI18n();
const { getUserDisplayName } = useAccessDisplayNames();

const { field, referencedItemKey, fieldValue, emitConnect, emitDisconnect, disabled, disabledNotice } = useRelationFormField<'to-one'>(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp, fieldId }));
// The users display template is `{{ fullName }}`; also fetch avatarId so the selected
// value renders the same avatar + name as the picker rows.
const { item, isLoading } = useM2OState(field, referencedItemKey, fieldValue, {
	additionalSelection: { fields: ['avatarId', 'email'] },
});
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const formBlockContext = injectFormBlockContext(null);
const permissions = useM2OPermissions({
	field,
	writeContext: () => formBlockContext?.writeContext.value,
});

const popoverOpen = ref(false);

// The selected value comes from useM2OState; it renders through UserAvatar's graceful fallbacks
// when the resolved display fields omit an avatar.
const selectedUser = computed<UserAvatarUser | null>(() => (item.value as UserAvatarUser | null) ?? null);

const selectedUserLabel = computed(() => (selectedUser.value ? getUserDisplayName(selectedUser.value) : ''));

const canOpen = computed(() => !disabled.value && !readonly && permissions.canConnect.value);

function isActiveMember(member: UserAvatarUser): boolean {
	return !!selectedUser.value && selectedUser.value.id === member.id;
}

function handleConnect(memberId: Uuid) {
	if (!field.value?.oppositeCollection) return;

	// The users collection is always primary-key-referenced
	emitConnect(toCompositePrimaryKey(memberId, field.value.oppositeCollection));
	popoverOpen.value = false;
}

function handleDeselect() {
	if (referencedItemKey.value) {
		emitDisconnect(referencedItemKey.value);
	}
}
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DFieldGroup>
			<UserPicker
				v-model:open="popoverOpen"
				:is-selected="isActiveMember"
				@select="handleConnect"
			>
				<DTooltip :disabled="disabled || permissions.canConnect.value" :text="t('permissions.no_connect_access')">
					<DButton
						class="
							h-10 min-w-0 flex-1 pe-1 text-start font-normal text-default ring-default
							hover:bg-default
							focus-visible:ring-primary
							disabled:ring-default!
							interactable-hover:ring-accented
						"
						color="neutral"
						variant="outline"
						data-testid="select"
						:disabled="!canOpen"
						:readonly
						:aria-readonly="readonly"
						:pressed="popoverOpen"
					>
						<div class="flex min-w-0 flex-1 items-center gap-2">
							<DSkeleton v-if="isLoading" class="h-6 w-40 rounded-sm opacity-0" />
							<template v-else-if="selectedUser">
								<UserAvatar :user="selectedUser" class="size-6 flex-none" />
								<span class="truncate text-sm font-medium text-default">{{ selectedUserLabel }}</span>
							</template>
							<span v-else class="font-normal text-dimmed select-none">
								{{ t('blocks.input_user_m2o.placeholder') }}
							</span>
						</div>
						<M2ODeselectButton
							v-if="selectedUser && !readonly && showRemove"
							:disabled="disabled || !permissions.canDisconnect.value"
							@deselect="handleDeselect"
						/>
					</DButton>
				</DTooltip>
			</UserPicker>
		</DFieldGroup>
	</DTooltip>
</template>
